import React, { useEffect, useRef, useState } from "react";
import "./styles.css";
import api from "../../Components/RefreshToken/RefreshToken";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MAIN_URL } from "../../constants";
import { formatServerDateTime } from "../../utils/serverDate";
import DashboardMain from "./FirstDashboard";
import SalesReport from "./SalesReport";
import Students from "./Students";
import {
    Phone,
    Mail,
    ChevronRight,
    RefreshCw,
    Users,
} from "lucide-react";
import "./SalesReport.css";
import {
    Badge,
    Button,
    Card,
    Drawer,
    EmptyState,
    PageHeader,
    Pagination,
    ProgressBar,
    SegmentedControl,
    Select,
    TableSkeleton,
} from "../../Components/ui";
import "./StudentsPage.css";
import { ExamedChapterChips, LastExamSummary } from "../../Components/Students/ExamWidgets";
import { CourseAccordion, CourseAccordionRow } from "../../Components/Students/CourseAccordion";
import { chapterPercent, isCanceledRequest } from "../../Components/Students/examData";
import NotEnrolledStudents from "./NotEnrolledStudents";
import { StudentsDateModal, StudentsFilterBar } from "./StudentsFilters";
import { useStudentTotals } from "./useStudentTotals";
import { daysSince, formatDuration, formatInactiveDays } from "../LearningActivity/activityFormat";


// "Kursga yozilganlar" tab'i — avvalgi Studentlar sahifasi (jadval, panel, filtrlar, sahifalash).
const EnrolledStudents = () => {
    const TOKEN = localStorage.getItem('admin_token');
    const [data, setData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [foundCount, setFoundCount] = useState(0);
    const rowsPerPage = 10;

    // 🔹 localStorage orqali saqlangan sahifani o'qish
    const savedPage = parseInt(localStorage.getItem("students_current_page") || "1", 10);
    const [currentPage, setCurrentPage] = useState(savedPage);

    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [showFilter, setShowFilter] = useState(false);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [courses, setCourses] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState("");
    const [panelOpen, setPanelOpen] = useState(false);
    const [student, setStudent] = useState(null);
    const [activeAccordion, setActiveAccordion] = useState(null);
    const navigate = useNavigate();

    // Panel: kurs bo'yicha imtihon ma'lumoti { [courseId]: { lastExam, examedChapters } },
    // har biri { loading, error, data }. Yangi student ochilganda tozalanadi.
    const [courseStatuses, setCourseStatuses] = useState({});
    const [panelRefreshing, setPanelRefreshing] = useState(false);
    const listRequestIdRef = useRef(0);
    const panelRefreshCountRef = useRef(0);
    const courseControllersRef = useRef({});

    // courseControllersRef.current obyekti qayta tayinlanmaydi — faqat kalitlari o'zgaradi.
    const abortCourseRequests = () => {
        const controllers = courseControllersRef.current;
        Object.keys(controllers).forEach((id) => {
            controllers[id].abort();
            delete controllers[id];
        });
    };

    useEffect(() => {
        const controllers = courseControllersRef.current;
        return () => Object.values(controllers).forEach((controller) => controller.abort());
    }, []);

    const openPanel = (data) => {
        setStudent(data);
        setPanelOpen(true);
        setActiveAccordion(null);
        abortCourseRequests();
        setCourseStatuses({});
        refreshPanel(data.id);
    };

    // silent: true — panelni yangilash uchun: jadval skeleton'ga o'tmaydi, xatoda jadval tozalanmaydi,
    // undan keyin yangi so'rov boshlangan bo'lsa natija qo'llanmaydi. Jadval chaqiruvlari avvalgidek.
    const fetchData = (page, searchQuery = "", dateFrom = "", dateTo = "", courseId = "", { silent = false } = {}) => {
        const requestId = ++listRequestIdRef.current;
        if (!silent) setLoading(true);
        const skip = (page - 1) * rowsPerPage;
        const params = { skip, limit: rowsPerPage };
        if (searchQuery.trim()) params.search = searchQuery;
        if (dateFrom) params.from_created_at = dateFrom;
        if (dateTo) params.to_created_at = dateTo;
        if (courseId) params.course_id = courseId;

        return api.get(`/management/students`, {
            params,
            headers: {
                accept: "application/json",
                Authorization: `Bearer ${TOKEN}`,
            },
        })
            .then((res) => {
                const result = res?.data.result || [];
                const total = res?.data.total_count || 0;
                // found — filtrlardan keyin topilganlar (sahifalash shu bo'yicha); total_count filtrlarga qaramaydi
                const found = typeof res?.data?.found === "number" ? res.data.found : total;
                if (silent && requestId !== listRequestIdRef.current) return null;
                setData(result);
                setTotalCount(total);
                setFoundCount(found);
                return result;
            })
            .catch((err) => {
                console.log(err);
                if (silent) return null;
                setData([]);
                setTotalCount(0);
                setFoundCount(0);
                return null;
            })
            .finally(() => {
                if (!silent) setLoading(false);
            });
    };

    // Panel ochilganda / "Yangilash": joriy ro'yxat sahifasi o'sha parametrlar bilan qayta olinadi,
    // panel shu student id bo'yicha yangilanadi (bitta student uchun endpoint yo'q).
    const refreshPanel = (studentId) => {
        panelRefreshCountRef.current += 1;
        setPanelRefreshing(true);
        fetchData(currentPage, search, fromDate, toDate, selectedCourse, { silent: true })
            .then((result) => {
                const fresh = result?.find((item) => item.id === studentId);
                if (fresh) setStudent((prev) => (prev && prev.id === studentId ? fresh : prev));
            })
            .finally(() => {
                panelRefreshCountRef.current -= 1;
                if (panelRefreshCountRef.current === 0) setPanelRefreshing(false);
            });
    };

    useEffect(() => {
        fetchData(currentPage, search, fromDate, toDate, selectedCourse);
    }, [currentPage, search, fromDate, toDate, selectedCourse]);


    useEffect(() => {
        api.get("/courses", {
            headers: {
                accept: "application/json",
                Authorization: `Bearer ${TOKEN}`,
            },
        })
            .then((res) => setCourses(res?.data || []))
            .catch((err) => console.log(err));
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setCurrentPage(1);
            localStorage.setItem("students_current_page", "1");
        }, 1000);

        return () => clearTimeout(timer);
    }, [search]);


    const totalPages = Math.ceil(foundCount / rowsPerPage);

    const handlePageChange = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
            localStorage.setItem("students_current_page", pageNumber.toString());
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    };

    // /exam-result?studentId=…&courseId=…[&chapterId=…] — sahifa yangilansa ham id'lar saqlanadi.
    const handleView = (id, courId, extra = {}) => {
        const query = new URLSearchParams({ studentId: id, courseId: courId });
        if (extra.chapterId !== undefined && extra.chapterId !== null) query.set("chapterId", extra.chapterId);
        navigate(`/exam-result?${query.toString()}`, {
            state: { id, courseId: courId, studentName: extra.studentName, courseTitle: extra.courseTitle },
        });
    };

    const hadleShow = (user) => {
        navigate(`/student-info`, { state: { user } });
    };

    // /learning-activity?learnerId=… — talabaning shu kursdagi o'qish faolligi
    const handleActivity = (learnerId) => {
        navigate(`/learning-activity?learnerId=${learnerId}`);
    };

    const handleApplyFilter = () => {
        setCurrentPage(1);
        localStorage.setItem("students_current_page", "1");
        setShowFilter(false);
    };

    const handleClearFilter = () => {
        setFromDate("");
        setToDate("");
        setSelectedCourse("");
        setCurrentPage(1);
        localStorage.setItem("students_current_page", "1");
        setShowFilter(false);
    };

    const handleCourseChange = (e) => {
        const newCourseId = e.target.value;
        setSelectedCourse(newCourseId);
        setCurrentPage(1);
        localStorage.setItem("students_current_page", "1");
    };


    const setCourseStatus = (courseId, key, value) => {
        setCourseStatuses((prev) => ({
            ...prev,
            [courseId]: { ...prev[courseId], [key]: value },
        }));
    };

    const getProgress = (learnerId, chapterOrder, courseId, signal) => {
        const token = localStorage.getItem("admin_token");

        setCourseStatus(courseId, "lastExam", { loading: true, error: null, data: null });
        api.get(
            `/management/students/${learnerId}/last-exam-result`,
            {
                params: {
                    chapter_order: chapterOrder,
                    course_id: courseId,
                },
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                signal,
            }
        )
            .then(response => {
                if (signal?.aborted) return;
                setCourseStatus(courseId, "lastExam", { loading: false, error: null, data: response.data });
            })
            .catch(error => {
                if (isCanceledRequest(error) || signal?.aborted) return;
                console.error("Error:", error.response?.data || error.message);
                setCourseStatus(courseId, "lastExam", { loading: false, error: true, data: null });
            });
    };

    // Imtihon topshirilgan boblar (ExamResult sahifasi ishlatadigan o'sha endpoint).
    const getExamedChapters = (studentId, courseId, signal) => {
        setCourseStatus(courseId, "examedChapters", { loading: true, error: null, data: null });
        api.get(`${MAIN_URL}/management/students/${studentId}/${courseId}/examed/chapters`, {
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${TOKEN}`,
            },
            signal,
        })
            .then((res) => {
                if (signal?.aborted) return;
                setCourseStatus(courseId, "examedChapters", { loading: false, error: null, data: res?.data || [] });
            })
            .catch((err) => {
                if (isCanceledRequest(err) || signal?.aborted) return;
                console.log(err);
                setCourseStatus(courseId, "examedChapters", { loading: false, error: true, data: null });
            });
    };

    // Akkordeon ochilganda: 2 ta so'rov. Shu kurs uchun oldingi so'rovlar bekor qilinadi.
    const loadCourseExamData = (course, studentId) => {
        courseControllersRef.current[course.id]?.abort();
        const controller = new AbortController();
        courseControllersRef.current[course.id] = controller;
        getProgress(course.learner_id, course.current_lesson.chapter_order, course.id, controller.signal);
        getExamedChapters(studentId, course.id, controller.signal);
    };

    const handlePanelRefresh = () => {
        if (!student) return;
        refreshPanel(student.id);
        const openCourse = activeAccordion !== null ? student.courses?.[activeAccordion] : null;
        if (openCourse) loadCourseExamData(openCourse, student.id);
    };


    return (
        <div className="ui-students__tab">
            {/* FILTER */}
            <StudentsFilterBar
                search={search}
                onSearchChange={(e) => setSearch(e.target.value)}
                hasDateFilter={Boolean(fromDate || toDate)}
                onToggleFilter={() => setShowFilter(!showFilter)}
                courseFilter={
                    <Select
                        aria-label="Kurs"
                        value={selectedCourse}
                        onChange={handleCourseChange}
                        wrapperClassName="ui-students__course"
                    >
                        <option value="">Barcha kurslar</option>
                        {courses.map((course) => (
                            <option key={course.id} value={course.id}>
                                {course.title}
                            </option>
                        ))}
                    </Select>
                }
            />

            <StudentsDateModal
                open={showFilter}
                onClose={() => setShowFilter(false)}
                fromDate={fromDate}
                toDate={toDate}
                onFromChange={(e) => setFromDate(e.target.value)}
                onToChange={(e) => setToDate(e.target.value)}
                onClear={handleClearFilter}
                onApply={handleApplyFilter}
            />

            {/* TABLE */}
            {loading ? (
                <TableSkeleton rows={rowsPerPage} cols={7} />
            ) : data.length === 0 ? (
                <Card padding="none">
                    <EmptyState
                        icon={<Users />}
                        title="Studentlar topilmadi"
                        description="Qidiruv yoki filtrlarni o'zgartirib ko'ring"
                    />
                </Card>
            ) : (
                <div className="ui-table-wrap">
                    <table className="ui-table ui-students__table">
                        <thead>
                            <tr>
                                <th>Student</th>
                                <th className="ui-table-cell--numeric">Kurslar</th>
                                <th>Faol kurs</th>
                                <th>Progress</th>
                                <th>So'nggi faollik</th>
                                <th>Status</th>
                                <th aria-label="Amallar" />
                            </tr>
                        </thead>

                        <tbody>
                            {data.map((s) => (
                                <tr key={s.id} onClick={() => openPanel(s)} className="ui-table-row--clickable">
                                    <td>
                                        <div className="ui-students__person">
                                            <span className="ui-students__avatar" aria-hidden="true">
                                                {s.first_name?.[0]}
                                                {s.last_name?.[0]}
                                            </span>
                                            <div className="ui-students__person-text">
                                                <span className="ui-students__name">
                                                    {s.first_name} {s.last_name}
                                                </span>
                                                <span className="ui-students__email">{s.email}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="ui-table-cell--numeric">{s.courses?.length || 0}</td>
                                    <td>
                                        <span className="ui-students__course-title" title={s.courses?.[0]?.title || undefined}>
                                            {s.courses?.[0]?.title || "—"}
                                        </span>
                                    </td>
                                    <td>
                                        <ProgressBar
                                            value={s.courses?.[0]?.progress || 0}
                                            showValue
                                            label="Progress"
                                            className="ui-students__progress"
                                        />
                                    </td>
                                    <td title={formatServerDateTime(s.last_activity_at) || undefined}>
                                        {formatInactiveDays(daysSince(s.last_activity_at))}
                                    </td>
                                    <td>
                                        {s.courses?.[0]?.status === "ACTIVE" ? (
                                            <Badge variant="success" size="sm">Faol</Badge>
                                        ) : s.courses?.[0]?.status ? (
                                            <Badge variant="neutral" size="sm">{s.courses?.[0]?.status}</Badge>
                                        ) : (
                                            <span className="ui-students__muted">—</span>
                                        )}
                                    </td>
                                    <td className="ui-students__actions">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            rightIcon={<ChevronRight />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                hadleShow(s);
                                            }}
                                        >
                                            Batafsil
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* PAGINATION */}
            <div className="ui-students__footer">
                <span className="ui-students__total">
                    Topildi: {foundCount} ta
                    <span className="ui-students__muted"> · Jami: {totalCount} ta</span>
                </span>
                {foundCount > rowsPerPage && (
                    <Pagination
                        page={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                )}
            </div>

            {/* SLIDE PANEL */}
            <Drawer
                open={panelOpen}
                onClose={() => setPanelOpen(false)}
                title="Student profili"
                loading={panelRefreshing}
                actions={
                    <Button
                        variant="ghost"
                        size="sm"
                        leftIcon={<RefreshCw className={panelRefreshing ? "ui-students-spin" : undefined} />}
                        onClick={handlePanelRefresh}
                        disabled={!student}
                    >
                        Yangilash
                    </Button>
                }
            >
                {student && (
                    <div className="ui-students-drawer">
                        <div className="ui-students-panel__profile">
                            <span className="ui-students__avatar ui-students__avatar--lg" aria-hidden="true">
                                {student.first_name?.[0]}
                                {student.last_name?.[0]}
                            </span>
                            <div className="ui-students__person-text">
                                <span className="ui-students-panel__name">
                                    {student.first_name} {student.last_name}
                                </span>
                                <span className="ui-students__email">
                                    Ro'yxatdan o'tdi: {formatServerDateTime(student.created_at) || "—"}
                                </span>
                            </div>
                        </div>

                        <div className="ui-students-panel__contacts">
                            <div className="ui-students-panel__contact">
                                <Phone aria-hidden="true" /> {student.phone_number}
                            </div>
                            <div className="ui-students-panel__contact">
                                <Mail aria-hidden="true" /> {student.email}
                            </div>
                        </div>

                        <h3 className="ui-students-panel__section-title">Sotib olingan kurslar</h3>

                        <div className="ui-course-list">
                            {student.courses?.map((course, index) => (
                                <CourseAccordion
                                    key={course.id}
                                    title={course.title}
                                    meta={`${course.current_lesson.chapter_order}-bob / ${course.number_of_chapters}`}
                                    open={activeAccordion === index}
                                    onToggle={() => {
                                        const willOpen = activeAccordion !== index;
                                        setActiveAccordion(willOpen ? index : null);
                                        if (willOpen) loadCourseExamData(course, student.id);
                                    }}
                                >
                                    <CourseAccordionRow label="Joriy bob">
                                        {course.current_lesson.chapter_order}-bob / {course.number_of_chapters}
                                    </CourseAccordionRow>
                                    <ProgressBar
                                        value={chapterPercent(course.current_lesson.chapter_order, course.number_of_chapters)}
                                        size="md"
                                        label="Joriy bob"
                                    />
                                    {course.progress > 0 && (
                                        <span className="ui-course-acc__note">Progress: {course.progress}%</span>
                                    )}
                                    <p className="ui-course-acc__lesson" title={course.current_lesson.title}>
                                        Joriy dars: {course.current_lesson.title}
                                    </p>
                                    <CourseAccordionRow label="So'nggi faollik">
                                        <span title={formatServerDateTime(course.last_activity_at) || undefined}>
                                            {formatInactiveDays(daysSince(course.last_activity_at))}
                                        </span>
                                    </CourseAccordionRow>
                                    <CourseAccordionRow label="Oxirgi 7 kunda ko'rgan">
                                        {formatDuration(course.last_7_days_seconds)}
                                    </CourseAccordionRow>

                                    <div className="ui-course-acc__box">
                                        <span className="ui-course-acc__box-title">
                                            Oxirgi imtihon · {course.current_lesson.chapter_order}-bob
                                        </span>
                                        <LastExamSummary
                                            status={courseStatuses[course.id]?.lastExam}
                                            onRetry={() => loadCourseExamData(course, student.id)}
                                        />
                                    </div>

                                    <div className="ui-course-acc__section">
                                        <span className="ui-course-acc__label">Imtihon topshirilgan boblar:</span>
                                        <ExamedChapterChips
                                            status={courseStatuses[course.id]?.examedChapters}
                                            onRetry={() => loadCourseExamData(course, student.id)}
                                            onSelect={(chapter) =>
                                                handleView(student.id, course.id, {
                                                    chapterId: chapter.id,
                                                    studentName: `${student.first_name} ${student.last_name}`,
                                                    courseTitle: course.title,
                                                })
                                            }
                                        />
                                    </div>

                                    <div className="ui-course-acc__actions">
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            rightIcon={<ChevronRight />}
                                            onClick={() =>
                                                handleView(student.id, course.id, {
                                                    studentName: `${student.first_name} ${student.last_name}`,
                                                    courseTitle: course.title,
                                                })
                                            }
                                        >
                                            Batafsil natijalar
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            rightIcon={<ChevronRight />}
                                            onClick={() => handleActivity(course.learner_id)}
                                        >
                                            O'qish faolligi
                                        </Button>
                                    </div>
                                </CourseAccordion>
                            ))}
                        </div>
                    </div>
                )}
            </Drawer>
        </div>
    );
};

// Studentlar sahifasi: "Kursga yozilganlar" / "Kursga yozilmaganlar" tab'lari. Tanlangan tab URL'da (?tab=not-enrolled).
const NOT_ENROLLED = "not-enrolled";
const withCount = (label, count) => (count === null ? label : `${label} (${count})`);

const CoursesTable = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = searchParams.get("tab") === NOT_ENROLLED ? NOT_ENROLLED : "enrolled";
    const totals = useStudentTotals();

    const handleTabChange = (value) => {
        const next = new URLSearchParams(searchParams);
        if (value === NOT_ENROLLED) next.set("tab", NOT_ENROLLED);
        else next.delete("tab");
        setSearchParams(next, { replace: true });
    };

    return (
        <div className="ui-students">
            <div className="ui-students__head">
                <PageHeader title="Studentlar" subtitle="Platformadagi barcha studentlar" />
                {totals.consistent && (
                    <p className="ui-students__summary">
                        Jami ro'yxatdan o'tganlar: {totals.registered} (kursga yozilgan: {totals.enrolled}, yozilmagan: {totals.notEnrolled})
                    </p>
                )}
            </div>

            <SegmentedControl
                aria-label="Studentlar ro'yxati"
                className="ui-students__tabs"
                options={[
                    { value: "enrolled", label: withCount("Kursga yozilganlar", totals.enrolled) },
                    { value: NOT_ENROLLED, label: withCount("Kursga yozilmaganlar", totals.notEnrolled) },
                ]}
                value={tab}
                onChange={handleTabChange}
            />

            {tab === NOT_ENROLLED ? <NotEnrolledStudents /> : <EnrolledStudents />}
        </div>
    );
};

export default CoursesTable;